import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  ChevronRight,
  ChefHat,
  CreditCard,
  QrCode,
  ScanLine,
  ShieldCheck,
  BellRing,
  Users,
  Mail,
  Phone,
} from 'lucide-react';

const CONTACT_EMAIL = 'hello@redspeed.com.au';
const CONTACT_PHONE = '+61412521711';
const CONTACT_PHONE_DISPLAY = '+61 412 521 711';

function LandingPage() {
  const [activeTab, setActiveTab] = useState(0);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Who actually uses GapGo today — not a fictional multi-vertical SaaS pitch.
  const segments = [
    {
      label: 'Guests',
      icon: ScanLine,
      eyebrow: 'Staying at the hotel',
      headline: 'Order without downloading anything.',
      copy: 'Scan the QR code in your room, browse the menu, pay with card or wallet, and watch your order move from kitchen to your door — no app, no account required.',
      points: ['One QR scan to start', 'Room dining from local restaurants', 'Live order tracking', 'Apple Pay & Google Pay supported'],
    },
    {
      label: 'Hotels',
      icon: Building2,
      eyebrow: 'Running the property',
      headline: 'A live view of every order, every room.',
      copy: 'Kitchen tickets land instantly, staff get real-time alerts, and your team gets a live dashboard of orders, revenue, and guest activity — no more relayed phone calls to reception.',
      points: ['Live kitchen ticket screen', 'Real-time orders & revenue dashboard', 'A QR code for every room', 'Menu managed from one place'],
    },
    {
      label: 'Restaurants',
      icon: ChefHat,
      eyebrow: 'Marketplace partners',
      headline: 'Reach every guest at the hotel, from one menu.',
      copy: 'Join the GapGo marketplace and your menu becomes available to guests at partner hotels — orders land straight in your kitchen, payments handled for you.',
      points: ["Darcy's is our first partner", 'One menu, every partner hotel', 'Orders sent straight to your kitchen', 'Stripe-secured payments'],
    },
  ];

  const active = segments[activeTab];

  const metrics = [
    { value: '4', label: 'Hotels live' },
    { value: '1 scan', label: 'For guests' },
    { value: 'Live', label: 'Kitchen tickets' },
  ];

  const features = [
    {
      icon: QrCode,
      title: 'One QR, zero downloads',
      desc: 'Guests open the same simple flow every time — ordering food or requesting a driver — straight from their phone’s browser.',
    },
    {
      icon: BellRing,
      title: 'Instant kitchen tickets',
      desc: 'The moment an order is placed it lands on the kitchen screen with an alert — no relayed calls, no missed orders.',
    },
    {
      icon: CreditCard,
      title: 'Payments built in',
      desc: 'Stripe-secured checkout is wired directly into ordering, including Apple Pay and Google Pay, so guests pay in seconds.',
    },
    {
      icon: Users,
      title: 'A live hotel dashboard',
      desc: 'Reception and management see today’s orders, revenue, and guest activity update in real time, per hotel.',
    },
    {
      icon: ChefHat,
      title: 'A growing restaurant marketplace',
      desc: 'Darcy’s is live today. GapGo is built to bring more local restaurants onto the same QR, checkout, and kitchen flow.',
    },
    {
      icon: ShieldCheck,
      title: 'Built to grow with you',
      desc: 'Live today across four hotels in the Grampians, on the same platform that’s ready for the next hotel, and the next town.',
    },
  ];

  const steps = [
    { n: '01', title: 'Talk to us', desc: 'Tell us about your hotel and how guests currently order room service today.' },
    { n: '02', title: 'We set you up', desc: 'We build your menu, print your room QR codes, and connect your kitchen screen.' },
    { n: '03', title: 'Go live', desc: 'Guests start ordering immediately, and your team gets a live dashboard from day one.' },
  ];

  const faqs = [
    { q: 'Who is GapGo for?', a: 'Hotels that want a faster, simpler way to run room service — and the guests staying in them.' },
    { q: 'Do guests need to download an app?', a: 'No. Everything runs in the browser from a single QR scan in the room.' },
    { q: 'How do payments work?', a: 'Checkout is Stripe-secured, with support for card, Apple Pay, and Google Pay.' },
    { q: 'Which hotels use GapGo today?', a: 'GapGo is live at Country Plaza, Pinnacle Holiday, Mountain View, and Kookaburra in Halls Gap, Victoria.' },
    { q: 'Can more restaurants join the marketplace?', a: 'Yes — Darcy’s is our first partner, with more local restaurants joining the same platform over time.' },
    { q: 'Is GapGo part of RedSpeed?', a: 'Yes — GapGo is built and operated by RedSpeed, alongside RedSpeed’s mobile and internet plans.' },
  ];

  return (
    <div className="lg">
      <style>{css}</style>

      <header className="lg-nav">
        <div className="lg-nav-in">
          <button className="lg-logo" onClick={() => scrollTo('top')}>
            Gap<span className="lg-logo-go">Go</span>
          </button>

          <nav className="lg-nav-links">
            <button className="lg-nl" onClick={() => scrollTo('who')}>Who it's for</button>
            <button className="lg-nl" onClick={() => scrollTo('platform')}>Platform</button>
            <button className="lg-nl" onClick={() => scrollTo('partner')}>Partner with us</button>
          </nav>

          <div className="lg-nav-right">
            {/* There was NO way for a restaurant to reach its own kitchen from
                anywhere on this site — "Partner with us" only scrolls to marketing
                copy. The single route in was being told the literal URL, and a
                typo bounced back to this page, which reads as "GapGo is broken".
                This is that missing door. */}
            <a className="lg-signin" href="/staff-login">Restaurant sign in</a>
            <button className="lg-signin" onClick={() => scrollTo('cta')}>Contact</button>
            <button className="lg-btn-red lg-btn-sm" onClick={() => scrollTo('cta')}>
              Talk to us
              <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ── Hero — black ── */}
        <section className="lg-hero">
          <div className="lg-hero-in">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg-hero-copy"
            >
              <div className="lg-badge">
                <span className="lg-badge-dot" />
                <span>Live today across 4 Halls Gap hotels</span>
              </div>

              <h1 className="lg-h1">
                Room service guests
                <br />
                actually enjoy.
              </h1>

              <p className="lg-sub">
                GapGo turns a single QR code into instant room dining and driver requests. Orders land straight in the kitchen — no app, no phone calls, no guessing who's next.
              </p>

              <div className="lg-hero-btns">
                <button className="lg-btn-red" onClick={() => scrollTo('cta')}>
                  Talk to us
                  <ArrowRight size={16} />
                </button>
                <button className="lg-btn-ghost" onClick={() => scrollTo('who')}>
                  See how it works
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="lg-stats">
                {metrics.map((m, i) => (
                  <React.Fragment key={m.label}>
                    <div className="lg-stat">
                      <span className="lg-stat-n">{m.value}</span>
                      <span className="lg-stat-l">{m.label}</span>
                    </div>
                    {i < metrics.length - 1 && <div className="lg-stat-sep" />}
                  </React.Fragment>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg-preview"
            >
              <div className="lg-preview-top">
                <div>
                  <p className="lg-preview-kicker">Live operation</p>
                  <h2 className="lg-preview-title">Country Plaza</h2>
                </div>
                <div className="lg-live-pill">
                  <span className="lg-live-dot" />
                  Live
                </div>
              </div>

              <div className="lg-preview-metrics">
                <div className="lg-mini-stat">
                  <span className="lg-mini-val">12</span>
                  <span className="lg-mini-lab">Open tickets</span>
                </div>
                <div className="lg-mini-stat">
                  <span className="lg-mini-val">4</span>
                  <span className="lg-mini-lab">Ready orders</span>
                </div>
                <div className="lg-mini-stat">
                  <span className="lg-mini-val">A$842</span>
                  <span className="lg-mini-lab">Today</span>
                </div>
              </div>

              <div className="lg-preview-flow">
                <div className="lg-flow-card">
                  <div className="lg-flow-head">
                    <span className="lg-flow-icon"><ScanLine size={15} /></span>
                    <span>Guest order</span>
                  </div>
                  <p>Room 312 placed a breakfast order.</p>
                </div>
                <div className="lg-flow-arrow"><ArrowRight size={15} /></div>
                <div className="lg-flow-card accent">
                  <div className="lg-flow-head">
                    <span className="lg-flow-icon"><ChefHat size={15} /></span>
                    <span>Kitchen ticket</span>
                  </div>
                  <p>Two tickets are now preparing.</p>
                </div>
              </div>

              <div className="lg-preview-list">
                <div className="lg-preview-row">
                  <span className="lg-row-pill new">New</span>
                  <span className="lg-row-text">Room 214 · Eggs Benedict</span>
                  <span className="lg-row-time">2m</span>
                </div>
                <div className="lg-preview-row">
                  <span className="lg-row-pill prep">Prep</span>
                  <span className="lg-row-text">Room 108 · Darcy's Burger</span>
                  <span className="lg-row-time">6m</span>
                </div>
                <div className="lg-preview-row">
                  <span className="lg-row-pill ready">Ready</span>
                  <span className="lg-row-text">Room 305 · Chicken Pasta</span>
                  <span className="lg-row-time">9m</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Who it's for — light ── */}
        <section className="lg-sec lg-light" id="who">
          <div className="lg-sec-in">
            <div className="lg-sec-head">
              <p className="lg-kicker">Who it's for</p>
              <h2 className="lg-h2">Three sides. One platform.</h2>
              <p className="lg-lede">Guests ordering, hotels running the floor, and the restaurants cooking it — all on the same rails.</p>
            </div>

            <div className="lg-seg-tabs">
              {segments.map((s, i) => {
                const SIcon = s.icon;
                return (
                  <button
                    key={s.label}
                    onClick={() => setActiveTab(i)}
                    className={'lg-seg-tab' + (activeTab === i ? ' on' : '')}
                  >
                    <div className="lg-seg-tab-icon"><SIcon size={20} /></div>
                    <span className="lg-seg-tab-label">{s.label}</span>
                    <span className="lg-seg-tab-eyebrow">{s.eyebrow}</span>
                  </button>
                );
              })}
            </div>

            <div className="lg-seg-card">
              <div>
                <h3 className="lg-seg-h">{active.headline}</h3>
                <p className="lg-seg-p">{active.copy}</p>
                <button className="lg-btn-red" onClick={() => scrollTo('cta')}>
                  Talk to us
                  <ArrowRight size={16} />
                </button>
              </div>
              <ul className="lg-seg-list">
                {active.points.map((p) => (
                  <li key={p}>
                    <span className="lg-seg-check"><ShieldCheck size={15} /></span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Platform — black ── */}
        <section className="lg-sec lg-dark" id="platform">
          <div className="lg-sec-in">
            <div className="lg-sec-head">
              <p className="lg-kicker">The platform</p>
              <h2 className="lg-h2">Quietly capable. Loud where it counts.</h2>
              <p className="lg-lede">Everything a hotel needs to run room service, and nothing it doesn't.</p>
            </div>

            <div className="lg-features">
              {features.map((f, i) => {
                const FIcon = f.icon;
                return (
                  <div key={f.title} className="lg-feat">
                    <div className="lg-feat-top">
                      <div className="lg-feat-icon"><FIcon size={18} /></div>
                      <span className="lg-feat-num">0{i + 1}</span>
                    </div>
                    <h3 className="lg-feat-t">{f.title}</h3>
                    <p className="lg-feat-d">{f.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="lg-quote">
              <p className="lg-quote-text">
                Halls Gap hotels didn't need another app to manage. They needed room service
                that just works — one scan, one kitchen ticket, one team that keeps moving.
              </p>
              <div className="lg-quote-attr">
                <span className="lg-quote-bar" />
                <span>Built in the Grampians, by RedSpeed</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Partner with us — light ── */}
        <section className="lg-sec lg-light" id="partner">
          <div className="lg-sec-in">
            <div className="lg-sec-head">
              <p className="lg-kicker">Partner with us</p>
              <h2 className="lg-h2">Getting GapGo into your hotel.</h2>
              <p className="lg-lede">Three steps, and usually live within days.</p>
            </div>

            <div className="lg-steps">
              {steps.map((s) => (
                <div key={s.n} className="lg-step">
                  <span className="lg-step-n">{s.n}</span>
                  <h3 className="lg-step-t">{s.title}</h3>
                  <p className="lg-step-d">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ — black ── */}
        <section className="lg-sec lg-dark" id="faq">
          <div className="lg-sec-in">
            <div className="lg-sec-head">
              <p className="lg-kicker">FAQ</p>
              <h2 className="lg-h2">Common questions.</h2>
            </div>
            <div className="lg-faqs">
              {faqs.map((f) => (
                <div key={f.q} className="lg-faq">
                  <h3 className="lg-faq-q">{f.q}</h3>
                  <p className="lg-faq-a">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA — light ── */}
        <section className="lg-sec lg-light" id="cta">
          <div className="lg-sec-in lg-cta-in">
            <p className="lg-kicker">Get started</p>
            <h2 className="lg-h2">Want GapGo running at your hotel?</h2>
            <p className="lg-lede">Tell us a bit about your property and we'll get you set up.</p>
            <div className="lg-cta-actions">
              <a className="lg-btn-red" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('GapGo enquiry')}`}>
                <Mail size={16} />
                {CONTACT_EMAIL}
              </a>
              <a className="lg-btn-ghost" href={`tel:${CONTACT_PHONE}`}>
                <Phone size={16} />
                {CONTACT_PHONE_DISPLAY}
              </a>
            </div>
            <p className="lg-cta-note">Already a guest? Scan the QR code in your room to order.</p>
          </div>
        </section>
      </main>

      {/* ── Footer — black ── */}
      <footer className="lg-footer">
        <div className="lg-footer-in">
          <div>
            <button className="lg-logo" onClick={() => scrollTo('top')} style={{ marginBottom: 10 }}>
              Gap<span className="lg-logo-go">Go</span>
            </button>
            <p className="lg-footer-tag">Room service guests actually enjoy.</p>
          </div>
          <div className="lg-footer-mid">
            <div>
              <p className="lg-footer-h">Product</p>
              <button className="lg-nl-ft" onClick={() => scrollTo('who')}>Who it's for</button>
              <button className="lg-nl-ft" onClick={() => scrollTo('platform')}>Platform</button>
              <button className="lg-nl-ft" onClick={() => scrollTo('partner')}>Partner with us</button>
            </div>
            <div>
              <p className="lg-footer-h">Contact</p>
              <a className="lg-nl-ft" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              <a className="lg-nl-ft" href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE_DISPLAY}</a>
              <a className="lg-nl-ft" href="/staff-login">Restaurant sign in</a>
            </div>
          </div>
          <div className="lg-footer-right">
            <p className="lg-footer-rs">Powered by RedSpeed</p>
            <p className="lg-footer-copy">RedSpeed Pty Ltd</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

.lg{
  --black:#000000;
  --light:#F5F5F7;
  --red:#E8001D;
  --red-deep:#B00016;
  /* on dark */
  --d-txt:#F5F5F7;
  --d-txt2:rgba(245,245,247,0.60);
  --d-txt3:rgba(245,245,247,0.40);
  --d-line:rgba(255,255,255,0.10);
  --d-card:rgba(255,255,255,0.04);
  /* on light */
  --l-txt:#1D1D1F;
  --l-txt2:rgba(29,29,31,0.62);
  --l-txt3:rgba(29,29,31,0.40);
  --l-line:rgba(0,0,0,0.08);
  --section-y:clamp(72px,10vw,128px);
  --round:44px;
  background:var(--black);
  color:var(--d-txt);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
  -webkit-font-smoothing:antialiased;
  overflow-x:hidden;
}
.lg *,.lg *::before,.lg *::after{box-sizing:border-box; margin:0; padding:0;}
.lg button{font-family:inherit; cursor:pointer; border:none; background:none; color:inherit;}
.lg a{text-decoration:none; color:inherit;}
.lg ul{list-style:none;}

/* ── Nav ── */
.lg-nav{position:sticky; top:0; z-index:100; background:rgba(0,0,0,0.85); backdrop-filter:blur(20px); -webkit-backdrop-filter:blur(20px); border-bottom:1px solid var(--d-line);}
.lg-nav-in{max-width:1100px; margin:0 auto; padding:13px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px;}
.lg-logo{font-weight:800; font-size:17px; letter-spacing:-0.5px; color:#fff; line-height:1;}
.lg-logo-go{color:var(--red);}
.lg-nav-links{display:flex; gap:2px;}
.lg-nl{color:var(--d-txt3); font-size:14px; font-weight:500; padding:8px 14px; border-radius:8px; transition:color .15s;}
.lg-nl:hover{color:var(--d-txt);}
.lg-nav-right{display:flex; align-items:center; gap:6px;}
.lg-signin{color:var(--d-txt2); font-size:14px; font-weight:500; padding:8px 12px;}
.lg-signin:hover{color:var(--d-txt);}

/* ── Buttons — flat, no glow ──
   Scoped with .lg so they outrank the ".lg button" reset above (which would
   otherwise strip background + border via higher specificity). */
.lg .lg-btn-red{display:inline-flex; align-items:center; justify-content:center; gap:8px; height:52px; padding:0 30px; border-radius:9999px; background:var(--red); color:#fff; font-size:15px; font-weight:600; letter-spacing:-0.01em; border:none; transition:background .2s;}
.lg .lg-btn-red:hover{background:var(--red-deep);}
.lg .lg-btn-sm{height:38px; padding:0 18px; font-size:14px;}
.lg .lg-btn-ghost{display:inline-flex; align-items:center; justify-content:center; gap:8px; height:52px; padding:0 30px; border-radius:9999px; background:transparent; font-size:15px; font-weight:600; letter-spacing:-0.01em; border:1px solid currentColor; transition:opacity .2s;}
.lg .lg-btn-ghost:hover{opacity:0.65;}

/* ── Section shell: each curves up over the one above (RedSpeed pattern) ── */
.lg-sec{position:relative; padding:var(--section-y) 24px; border-radius:var(--round) var(--round) 0 0; margin-top:calc(var(--round) * -1);}
.lg-sec-in{max-width:1100px; margin:0 auto;}
.lg-light{background:var(--light); color:var(--l-txt); z-index:2;}
.lg-dark{background:var(--black); color:var(--d-txt); z-index:3;}
.lg-light + .lg-dark{z-index:3;}
#platform{z-index:3;} #partner{z-index:4;} #faq{z-index:5;} #cta{z-index:6;}

.lg-sec-head{text-align:center; max-width:660px; margin:0 auto 56px;}
.lg-kicker{text-transform:uppercase; letter-spacing:0.12em; font-size:clamp(17px,2.2vw,24px); font-weight:800; color:var(--red); margin-bottom:16px;}
.lg-h2{font-size:clamp(32px,5vw,52px); font-weight:800; letter-spacing:-0.03em; line-height:1.05; margin-bottom:18px;}
.lg-lede{font-size:clamp(15px,1.6vw,18px); line-height:1.6;}
.lg-light .lg-lede{color:var(--l-txt2);}
.lg-dark .lg-lede{color:var(--d-txt2);}

/* ── Hero ── */
.lg-hero{max-width:1100px; margin:0 auto; padding:80px 24px 96px;}
.lg-hero-in{display:grid; grid-template-columns:minmax(0,1.05fr) minmax(340px,0.95fr); gap:32px; align-items:center;}
.lg-badge{display:inline-flex; align-items:center; gap:9px; font-size:13px; font-weight:500; color:var(--d-txt2); padding:7px 16px; border-radius:9999px; border:1px solid var(--d-line); margin-bottom:28px;}
.lg-badge-dot{width:6px; height:6px; border-radius:50%; background:var(--red); animation:pulse 2s ease infinite;}
@keyframes pulse{0%,100%{opacity:1;}50%{opacity:0.35;}}
.lg-h1{font-size:clamp(40px,5.6vw,68px); font-weight:800; line-height:1.0; letter-spacing:-0.035em; color:#fff; margin-bottom:22px;}
.lg-sub{font-size:18px; line-height:1.65; color:var(--d-txt2); max-width:560px; margin-bottom:32px;}
.lg-hero-btns{display:flex; gap:12px; margin-bottom:40px; flex-wrap:wrap; color:var(--d-txt);}
.lg-stats{display:inline-flex; align-items:center; border:1px solid var(--d-line); border-radius:18px; flex-wrap:wrap;}
.lg-stat{padding:18px 32px; text-align:center;}
.lg-stat-n{display:block; font-size:22px; font-weight:800; margin-bottom:3px; letter-spacing:-0.5px; color:#fff;}
.lg-stat-l{display:block; font-size:12px; color:var(--d-txt3);}
.lg-stat-sep{width:1px; align-self:stretch; background:var(--d-line);}

/* Live-operation preview card */
.lg-preview{border:1px solid var(--d-line); border-radius:24px; padding:22px; display:flex; flex-direction:column; gap:16px; background:rgba(255,255,255,0.02);}
.lg-preview-top{display:flex; align-items:flex-start; justify-content:space-between; gap:14px;}
.lg-preview-kicker{font-size:11px; letter-spacing:1.6px; text-transform:uppercase; color:var(--d-txt3); margin-bottom:6px;}
.lg-preview-title{font-size:26px; line-height:1.05; letter-spacing:-1px; color:#fff;}
.lg-live-pill{display:inline-flex; align-items:center; gap:7px; padding:7px 12px; border-radius:9999px; border:1px solid var(--d-line); color:#fff; font-size:12px; font-weight:600;}
.lg-live-dot{width:6px; height:6px; border-radius:50%; background:var(--red);}
.lg-preview-metrics{display:grid; grid-template-columns:repeat(3,1fr); gap:10px;}
.lg-mini-stat{padding:14px 12px; border-radius:14px; background:var(--d-card); border:1px solid var(--d-line);}
.lg-mini-val{display:block; font-size:21px; font-weight:800; letter-spacing:-0.7px; color:#fff; margin-bottom:2px;}
.lg-mini-lab{display:block; font-size:11.5px; color:var(--d-txt3);}
.lg-preview-flow{display:grid; grid-template-columns:1fr auto 1fr; gap:10px; align-items:center;}
.lg-flow-card{border-radius:14px; padding:14px; background:var(--d-card); border:1px solid var(--d-line);}
.lg-flow-card.accent{border-color:rgba(232,0,29,0.35);}
.lg-flow-head{display:flex; align-items:center; gap:8px; font-size:12.5px; color:#fff; font-weight:700; margin-bottom:7px;}
.lg-flow-icon{width:24px; height:24px; border-radius:7px; display:inline-flex; align-items:center; justify-content:center; background:rgba(255,255,255,0.06); color:var(--red);}
.lg-flow-card p{font-size:12.5px; line-height:1.5; color:var(--d-txt2);}
.lg-flow-arrow{width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:var(--d-txt3); border:1px solid var(--d-line);}
.lg-preview-list{display:flex; flex-direction:column; gap:8px;}
.lg-preview-row{display:grid; grid-template-columns:auto 1fr auto; gap:12px; align-items:center; padding:12px 14px; border-radius:14px; background:var(--d-card); border:1px solid var(--d-line);}
.lg-row-pill{font-size:10.5px; font-weight:800; letter-spacing:0.7px; text-transform:uppercase; padding:4px 8px; border-radius:9999px;}
.lg-row-pill.new{background:rgba(232,0,29,0.15); color:#ff7d91;}
.lg-row-pill.prep{background:rgba(255,255,255,0.08); color:var(--d-txt2);}
.lg-row-pill.ready{background:rgba(255,255,255,0.14); color:#fff;}
.lg-row-text{font-size:13.5px; color:#fff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.lg-row-time{font-size:11.5px; color:var(--d-txt3);}

/* ── Who it's for (light) ── */
.lg-seg-tabs{display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:20px;}
.lg .lg-seg-tab{border-radius:16px; padding:20px; display:flex; flex-direction:column; align-items:flex-start; gap:8px; background:#fff; border:1px solid var(--l-line); transition:border-color .2s;}
.lg .lg-seg-tab.on{border-color:var(--red);}
.lg-seg-tab-icon{color:var(--l-txt3);}
.lg-seg-tab.on .lg-seg-tab-icon{color:var(--red);}
.lg-seg-tab-label{font-size:17px; font-weight:700; letter-spacing:-0.3px; color:var(--l-txt);}
.lg-seg-tab-eyebrow{font-size:12px; color:var(--l-txt3);}
.lg-seg-card{display:grid; grid-template-columns:1.05fr 0.95fr; gap:40px; border-radius:22px; padding:38px; background:#fff; border:1px solid var(--l-line); color:var(--l-txt);}
.lg-seg-h{font-size:clamp(24px,3vw,32px); font-weight:800; letter-spacing:-0.02em; line-height:1.1; margin-bottom:14px;}
.lg-seg-p{font-size:16px; color:var(--l-txt2); line-height:1.7; margin-bottom:26px;}
.lg-seg-list{display:flex; flex-direction:column; gap:15px; justify-content:center;}
.lg-seg-list li{display:flex; align-items:flex-start; gap:12px; font-size:15px; line-height:1.55; color:var(--l-txt);}
.lg-seg-check{flex-shrink:0; margin-top:1px; color:var(--red);}

/* ── Platform (dark) ── */
.lg-features{display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:16px;}
.lg-feat{padding:28px 24px; border-radius:18px; background:var(--d-card); border:1px solid var(--d-line);}
.lg-feat-top{display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px;}
.lg-feat-icon{width:34px; height:34px; border-radius:10px; background:rgba(232,0,29,0.10); border:1px solid rgba(232,0,29,0.22); display:flex; align-items:center; justify-content:center; color:var(--red);}
.lg-feat-num{font-size:12px; font-weight:700; letter-spacing:1px; color:var(--red);}
.lg-feat-t{font-size:17px; font-weight:700; margin-bottom:9px; letter-spacing:-0.2px; color:#fff;}
.lg-feat-d{font-size:14px; color:var(--d-txt2); line-height:1.6;}
.lg-quote{max-width:760px; margin:64px auto 0; text-align:center;}
.lg-quote-text{font-size:clamp(20px,2.6vw,28px); font-weight:600; line-height:1.45; letter-spacing:-0.02em; color:#fff; margin-bottom:22px;}
.lg-quote-attr{display:inline-flex; align-items:center; gap:12px; font-size:13px; color:var(--d-txt3);}
.lg-quote-bar{width:22px; height:2px; background:var(--red);}

/* ── Partner steps (light) ── */
.lg-steps{display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:20px;}
.lg-step{background:#fff; border:1px solid var(--l-line); border-radius:18px; padding:32px 28px;}
.lg-step-n{display:block; font-size:40px; font-weight:800; letter-spacing:-0.03em; color:var(--red); margin-bottom:16px;}
.lg-step-t{font-size:20px; font-weight:700; letter-spacing:-0.02em; margin-bottom:10px; color:var(--l-txt);}
.lg-step-d{font-size:15px; color:var(--l-txt2); line-height:1.6;}

/* ── FAQ (dark) ── */
.lg-faqs{display:grid; grid-template-columns:1fr 1fr; gap:16px;}
.lg-faq{padding:26px; border-radius:16px; background:var(--d-card); border:1px solid var(--d-line);}
.lg-faq-q{font-size:16px; font-weight:700; margin-bottom:9px; color:#fff;}
.lg-faq-a{font-size:14px; color:var(--d-txt2); line-height:1.6;}

/* ── CTA (light) ── */
.lg-cta-in{text-align:center; max-width:660px;}
.lg-cta-actions{display:flex; gap:12px; justify-content:center; flex-wrap:wrap; margin-top:34px; color:var(--l-txt);}
.lg-cta-note{font-size:13.5px; color:var(--l-txt3); margin-top:20px;}

/* ── Footer ── */
.lg-footer{position:relative; z-index:7; background:var(--black); border-radius:var(--round) var(--round) 0 0; margin-top:calc(var(--round) * -1); padding:56px 24px 36px; border-top:1px solid var(--d-line);}
.lg-footer-in{max-width:1100px; margin:0 auto; display:flex; justify-content:space-between; gap:40px; flex-wrap:wrap;}
.lg-footer-tag{font-size:13px; color:var(--d-txt3);}
.lg-footer-mid{display:flex; gap:64px;}
.lg-footer-h{font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--d-txt3); margin-bottom:14px;}
.lg-nl-ft{display:block; font-size:14px; color:var(--d-txt2); padding:5px 0; text-align:left;}
.lg-nl-ft:hover{color:var(--d-txt);}
.lg-footer-right{text-align:right;}
.lg-footer-rs{font-size:12px; font-weight:600; color:var(--d-txt2); margin-bottom:4px;}
.lg-footer-copy{font-size:12px; color:var(--d-txt3);}

@media (max-width: 980px){
  .lg-hero-in{grid-template-columns:1fr; gap:36px;}
  .lg-preview{order:-1;}
  .lg-seg-tabs,.lg-faqs{grid-template-columns:1fr;}
  .lg-seg-card{grid-template-columns:1fr; gap:28px; padding:30px;}
  .lg-nav-links{display:none;}
  .lg-hero{padding:56px 20px 72px;}
  .lg-sec{padding-left:20px; padding-right:20px;}
  .lg-footer-mid{gap:40px;}
}

@media (max-width: 560px){
  .lg-nav-in{padding:12px 16px;}
  .lg-hero-btns{flex-direction:column; align-items:stretch;}
  .lg-stats{display:grid; grid-template-columns:1fr; width:100%;}
  .lg-stat-sep{display:none; }
  .lg-stat{padding:15px 18px; border-bottom:1px solid var(--d-line);}
  .lg-preview-metrics{grid-template-columns:1fr;}
  .lg-preview-flow{grid-template-columns:1fr;}
  .lg-flow-arrow{display:none;}
  .lg-preview-row{grid-template-columns:auto 1fr;}
  .lg-row-time{grid-column:2; justify-self:end;}
  .lg-cta-actions{flex-direction:column;}
  .lg-footer-right{text-align:left;}
}
`;

export default LandingPage;
